import { Box, CircularProgress, IconButton, Skeleton, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { showQuery } from '../../connections/getApi';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LinkIcon from '@mui/icons-material/Link';
import { postReplyQuery } from '../../connections/postApi';
import { useSelector } from 'react-redux';
import StopIcon from '@mui/icons-material/Stop';
import DeleteIcon from '@mui/icons-material/Delete';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import DoneIcon from "@mui/icons-material/Done";
import ChatIcon from '@mui/icons-material/Chat';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { updateQueryStatus } from '../../connections/putApi';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);


const ChatCardQuery = ({ id, fromParticularPatient = false }) => {
    const prescriptionData = useSelector(
        (state) => state.prescription.prescriptionData
    );

    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const flatListRef = useRef(null);
    const [playingUrl, setPlayingUrl] = useState(null);
    const [audio, setAudio] = useState(null);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [recordedUri, setRecordedUri] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [micStream, setMicStream] = useState(null);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const [audioMimeType, setAudioMimeType] = useState('');
    const [queryStatus, setQueryStatus] = useState('');
    const [anchorEl, setAnchorEl] = React.useState(null);


    const fileInputRef = useRef();

    // ---------- 📜 Fetch Messages ----------
    const handleFetchConversation = async () => {
        try {
            setLoadingMessages(true);
            const response = await showQuery(id);
            setQueryStatus(response?.payload?.status);
            if (response?.payload?.query_replies) {
                setMessages(response?.payload?.query_replies);
            }
        } catch (error) {
            console.error("Error fetching conversation:", error);
        }
        finally {
            setLoadingMessages(false);

        }
    };

    // Auto-scroll to bottom when new message arrives
    useEffect(() => {
        if (flatListRef.current) {
            flatListRef.current.scrollTop = flatListRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (id) {
            handleFetchConversation();
        }
    }, [id]);

    // const handleSend = async () => {
    //     if (!inputText?.trim() && !selectedFile) return;

    //     setLoading(true);
    //     const formData = new FormData();
    //     formData.append("query_reply[message]", inputText || "");

    //     try {
    //         // ✅ Handle file attachment (images or PDF)
    //         if (selectedFile) {
    //             const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
    //             const allowedDocType = "application/pdf";

    //             if (
    //                 !allowedImageTypes.includes(selectedFile.type) &&
    //                 selectedFile.type !== allowedDocType
    //             ) {
    //                 alert("Invalid file type. Please upload JPG, PNG, or PDF files only.");
    //                 setLoading(false);
    //                 return;
    //             }

    //             // ✅ selectedFile is already a File object from <input type="file" />
    //             formData.append("query_reply[attachments][]", selectedFile);
    //         }

    //         // ✅ Handle recorded audio (if any)
    //         if (recordedUri) {
    //             const responseBlob = await fetch(recordedUri).then((r) => r.blob());
    //             const audioFile = new File([responseBlob], `voice_${Date.now()}.m4a`, {
    //                 type: "audio/m4a",
    //             });
    //             formData.append("query_reply[attachments][]", audioFile);
    //         }

    //         // ✅ Send API request
    //         const response = await postReplyQuery(id, prescriptionData?.id, formData,);

    //         if (response?.status?.status) {
    //             const lastMessage =
    //                 response?.payload?.query?.query_replies?.[
    //                 response?.payload?.query?.query_replies?.length - 1
    //                 ];

    //             if (lastMessage) {
    //                 setMessages((prev) => [...prev, lastMessage]);
    //             }

    //             setInputText("");
    //             setSelectedFile(null);
    //             setRecordedUri(null);
    //         } else {
    //             alert("❌ Failed to send message. Please try again later.");
    //         }
    //     } catch (error) {
    //         console.error("Send Message Error:", error);
    //         alert(`⚠️ Message failed: ${error.message || error.toString()}`);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // Format timestamp

    const handleSend = async () => {
        if (!inputText?.trim() && !selectedFile && !(audioChunks && audioChunks.length)) return;

        setLoading(true);
        const formData = new FormData();
        formData.append("query_reply[message]", inputText || "");

        try {
            // ✅ Handle file attachment (images or PDF)
            if (selectedFile) {
                const allowedImageTypes = ["image/jpeg", "image/jpg", "image/png"];
                const allowedDocType = "application/pdf";

                if (
                    !allowedImageTypes.includes(selectedFile.type) &&
                    selectedFile.type !== allowedDocType
                ) {
                    alert("Invalid file type. Please upload JPG, PNG, or PDF files only.");
                    setLoading(false);
                    return;
                }

                formData.append("query_reply[attachments][]", selectedFile);
            }

            // ✅ Handle recorded audio as BINARY from in-memory chunks
            if (audioChunks && audioChunks.length) {
                const mime =
                    (mediaRecorder && mediaRecorder.mimeType) ||
                    "audio/webm"; // sensible default for most browsers

                const audioBlob = new Blob(audioChunks, { type: mime });

                const ext =
                    mime.includes("webm") ? "webm" :
                        mime.includes("ogg") ? "ogg" :
                            mime.includes("mp4") ? "m4a" :
                                mime.includes("mpeg") ? "mp3" : "webm";

                const audioFile = new File(
                    [audioBlob],
                    `voice_${Date.now()}.${ext}`,
                    { type: mime }
                );

                formData.append("query_reply[attachments][]", audioFile);
            }

            // ✅ Send API request
            const response = await postReplyQuery(id, prescriptionData?.id, formData);

            if (response?.status?.status) {
                const lastMessage =
                    response?.payload?.query?.query_replies?.[
                    response?.payload?.query?.query_replies?.length - 1
                    ];

                if (lastMessage) {
                    setMessages((prev) => [...prev, lastMessage]);
                }

                // reset inputs
                setInputText("");
                setSelectedFile(null);
                if (recordedUri) URL.revokeObjectURL(recordedUri);
                setRecordedUri(null);
                setAudioChunks([]);
                setAudio(null);
                setPlayingUrl(null);
            } else {
                alert("❌ Failed to send message. Please try again later.");
            }
        } catch (error) {
            console.error("Send Message Error:", error);
            alert(`⚠️ Message failed: ${error.message || error.toString()}`);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return dayjs(dateStr).tz("Asia/Kolkata").format("DD/MM/YYYY, hh:mm:ss A");
    };

    // 🖼️ Render Attachments (React Web)
    const renderAttachments = (attachments) => {
        if (!attachments || attachments.length === 0) return null;

        return attachments?.map((att, index) => {
            const type = att?.content_type || "";

            // 🖼️ Image attachments
            if (type?.includes("image")) {
                return (
                    <Box
                        key={index}
                        sx={{
                            display: "inline-block",
                            margin: "6px 0",
                            borderRadius: "10px",
                            // overflow: "hidden",

                            /* 🔒 Hide scrollbar */
                            scrollbarWidth: "none",          // Firefox
                            msOverflowStyle: "none",          // IE & Edge
                            "&::-webkit-scrollbar": {
                                display: "none",              // Chrome, Safari
                            },
                            cursor: "pointer",
                        }}
                        onClick={() => window.open(att?.url, "_blank")}
                    >
                        <img
                            src={att?.url}
                            alt={att?.filename || "Image"}
                            style={{
                                width: 200,
                                height: 200,
                                objectFit: "cover",
                                background: "#ddd",
                            }}
                        />
                    </Box>
                );
            }

            // 📄 PDF attachments
            if (type.includes("pdf")) {
                return (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: "#F5F5F5",
                            borderRadius: "10px",
                            p: 1,
                            my: 1,
                            gap: 1,
                            cursor: "pointer",
                        }}
                        onClick={() => window.open(att.url, "_blank")}
                    >
                        <PictureAsPdfIcon color="error" />
                        <Typography variant="body2" color={"#2E37A4"}>{att.filename || "Open PDF"}</Typography>
                    </Box>
                );
            }

            // 🎵 Audio attachments
            if (type.includes("audio")) {
                const isPlayingThis = playingUrl === att.url;

                const togglePlay = () => {
                    if (isPlayingThis) {
                        audio.pause();
                        setPlayingUrl(null);
                    } else {
                        const newAudio = new Audio(att.url);
                        newAudio.play();
                        setAudio(newAudio);
                        setPlayingUrl(att.url);
                        newAudio.onended = () => setPlayingUrl(null);
                    }
                };

                return (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            background: "#f0f0f0",
                            borderRadius: "10px",
                            p: 1,
                            my: 1,
                            justifyContent: "space-between",
                            width: "fit-content",
                        }}
                    >
                        <IconButton size="small" onClick={togglePlay}>
                            {/* <AudiotrackIcon color="primary" /> */}
                            < AudioFileIcon color="primary" />
                        </IconButton>

                        {isPlayingThis ?
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <PauseOutlinedIcon sx={{ color: "#2E37A4" }} />
                                <Typography variant="body2" color={"#2E37A4"}>
                                    Pause Voice Message
                                </Typography>
                            </Box>
                            :
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                <PlayArrowOutlinedIcon sx={{ color: "#2E37A4" }} />
                                <Typography variant="body2" color={"#2E37A4"}>
                                    Play Voice Message
                                </Typography>
                            </Box>
                        }
                    </Box>
                );
            }

            // 🌐 Default file attachment
            return (
                <Box
                    key={index}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: "#eee",
                        borderRadius: "10px",
                        padding: "6px",
                        my: 1,
                        cursor: "pointer",
                    }}
                    onClick={() => window.open(att.url, "_blank")}
                >
                    <LinkIcon color="action" />
                    <Typography variant="body2">{att.filename || "View File"}</Typography>
                </Box>
            );
        });
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicStream(stream);

            const candidates = [
                'audio/webm;codecs=opus',
                'audio/ogg;codecs=opus',
                'audio/mp4',            // Safari
                'audio/mpeg'            // mp3 fallback if supported
            ];
            const chosen = candidates.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
            setAudioMimeType(chosen);

            const recorder = new MediaRecorder(stream, chosen ? { mimeType: chosen } : undefined);
            setMediaRecorder(recorder);
            setIsRecording(true);
            setAudioChunks([]);

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) setAudioChunks(prev => [...prev, e.data]);
            };

            recorder.start();
        } catch (err) {
            console.error("Microphone access denied:", err);
            alert("Please allow microphone access to record voice messages.");
        }
    };

    const stopRecording = () => {
        if (!mediaRecorder) return;

        mediaRecorder.onstop = () => {
            const type = audioMimeType || mediaRecorder.mimeType || 'audio/webm';
            const blob = new Blob(audioChunks, { type });
            setRecordedBlob(blob);
            const url = URL.createObjectURL(blob);
            setRecordedUri(url);
            setIsRecording(false);
        };

        if (mediaRecorder.state !== 'inactive') mediaRecorder.stop();

        if (micStream) {
            micStream.getTracks().forEach(track => track.stop());
            setMicStream(null);
        }
    };

    // 🗑 Cancel Recording (discard)
    const cancelRecording = () => {
        if (mediaRecorder && mediaRecorder.state !== "inactive") mediaRecorder.stop();
        if (micStream) {
            micStream.getTracks().forEach(track => track.stop());
            setMicStream(null);
        }
        if (recordedUri) URL.revokeObjectURL(recordedUri);
        setRecordedBlob(null);
        setIsRecording(false);
        setRecordedUri(null);
        setAudioChunks([]);
    };

    const getStatusUI = (status) => {
        switch (status) {
            case 'open':
                return {
                    text: 'Open',
                    color: '#1976d2',
                    bg: '#e3f2fd',
                    Icon: ChatIcon
                };

            case 'resolved':
                return {
                    text: 'Resolved',
                    color: '#4DD57B',
                    bg: '#b4eec8',
                    Icon: DoneIcon
                };

            case 'close':
                return {
                    text: 'Closed',
                    color: '#e53935',
                    bg: '#ffebee',
                    Icon: CloseIcon
                };

            case 'in_progress':
                return {
                    text: 'In Progress',
                    color: '#fb8c00',
                    bg: '#fff3e0',
                    Icon: AutorenewIcon
                };

            default:
                return {
                    text: 'Open',
                    color: '#1976d2',
                    bg: '#e3f2fd',
                    Icon: ChatIcon
                };
        }
    };

    const handleStatusClick = (event, queryId) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    // ✅ One function to change status
    const handleChangeStatus = async (status, id) => {
        // console.log("Query ID:", selectedQueryId, "New Status:", status);
        // handleCloseMenu();
        const payload = {
            query: {
                status: status
            }
        };
        try {
            const res = await updateQueryStatus(payload, id);
            console.log(res, 'res');
        } catch (error) {
            console.log(error);
        }
    };

    const { text, color, bg } = getStatusUI(queryStatus);

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                // pr: 1,
                // mb: 2,
            }}
        >

            {loadingMessages ?
                (
                    <>
                        {[...Array(4)].map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    alignSelf: i % 2 === 0 ? "flex-start" : "flex-end",
                                    backgroundColor: i % 2 === 0 ? "#F1F1F1" : "neutral.teal",
                                    color: i % 2 === 0 ? "black" : "white",
                                    borderRadius: "10px",
                                    p: 1.5,
                                    flex: 1,                 // 🔑 takes remaining height
                                    maxWidth: "75%",
                                }}
                            >
                                <Skeleton variant="text" width={120} />
                                <Skeleton variant="rectangular" height={20} width={200} sx={{ borderRadius: "6px" }} />
                            </Box>
                        ))}
                    </>
                )
                : messages?.length === 0 ? (
                    /* 🟡 EMPTY STATE */
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#888",
                            textAlign: "center",
                            px: 2,
                        }}
                    >
                        <Typography variant="body2">
                            No messages yet.
                            <br />
                            Start the conversation 👋
                        </Typography>
                    </Box>)
                    : (
                        <Box
                            ref={flatListRef}
                            sx={{
                                ...(fromParticularPatient
                                    ? { maxHeight: "350px" }
                                    : { flex: 1 }),
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                padding: "10px",
                                background: "#fff",
                                borderRadius: "8px",

                                /* 🔒 Hide scrollbar */
                                scrollbarWidth: "none",          // Firefox
                                msOverflowStyle: "none",          // IE & Edge
                                "&::-webkit-scrollbar": {
                                    display: "none",              // Chrome, Safari
                                },
                            }}
                        >
                            {messages?.map((item, index) => {
                                const isUser = item?.repliable_type !== "Patient";
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            alignSelf: isUser ? "flex-end" : "flex-start",
                                            backgroundColor: isUser ? "neutral.teal" : "#F1F1F1",
                                            color: isUser ? "white" : "black",
                                            borderRadius: "10px",
                                            p: 1.5,
                                            maxWidth: "75%",
                                        }}
                                    >
                                        {/* Attachments */}
                                        {renderAttachments(item?.attachments)}

                                        {/* Message */}
                                        {item.message && (
                                            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                                                {item.message}
                                            </Typography>
                                        )}

                                        {/* Timestamp */}
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: "block",
                                                textAlign: "right",
                                                color: isUser ? "#E0E0E0" : "#777",
                                                mt: 0.5,
                                            }}
                                        >
                                            {formatDate(item?.created_at)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}


            {/* 📎 Selected File Preview */}
            {selectedFile && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#f5f5f5",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        mb: 1,
                    }}
                >
                    {selectedFile.type.startsWith("image/") ? (
                        <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="Preview"
                            style={{
                                width: 50,
                                height: 50,
                                borderRadius: 6,
                                objectFit: "cover",
                            }}
                        />
                    ) : (
                        <span style={{ flex: 1 }}>{selectedFile.name}</span>
                    )}
                    <IconButton
                        color="error"
                        onClick={() => setSelectedFile(null)}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {/* 🎤 --- Voice Recording & Input Section --- */}
            {isRecording || recordedUri ? (
                <>
                    {/* ▶️ Preview bar when recording stopped */}
                    {recordedUri && !isRecording && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: "#f9f9f9",
                                borderTop: "1px solid #ddd",
                                padding: "10px 16px",
                                borderRadius: '10px',
                            }}
                        >
                            {/* ▶️ / ⏸️ Toggle + Delete */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                {/* <IconButton
                                        color="primary"
                                        onClick={() => {
                                            if (audio) {
                                                audio.pause();
                                                setAudio(null);
                                                setPlayingUrl(null);
                                            } else {
                                                const newAudio = new Audio(recordedUri);
                                                setAudio(newAudio);
                                                setPlayingUrl(recordedUri);
                                                newAudio.play();
                                                newAudio.onended = () => {
                                                    setAudio(null);
                                                    setPlayingUrl(null);
                                                };
                                            }
                                        }}
                                    >
                                        {audio ? <PauseIcon /> : <PlayArrowIcon />}
                                    </IconButton> */}

                                {/* 🗑️ Delete Recording */}
                                <IconButton
                                    color="error"
                                    onClick={cancelRecording}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            {/* ✅ Send Recorded Audio */}
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={loading}
                                sx={{
                                    backgroundColor: "neutral.teal",
                                    color: "white",
                                    "&:hover": { backgroundColor: "neutral.teal" },
                                }}
                            >
                                {loading ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
                            </IconButton>
                        </Box>
                    )}

                    {/* 🔴 Active Recording Bar */}
                    {isRecording && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                backgroundColor: "#fff0f0",
                                borderTop: "1px solid #ddd",
                                padding: "12px 16px",
                                borderRadius: "10px"
                            }}
                        >
                            {/* 🗑️ Cancel Recording */}
                            <IconButton
                                sx={{
                                    backgroundColor: "red",
                                    color: "white",
                                    "&:hover": { backgroundColor: "#c40000" },
                                }}
                                onClick={cancelRecording}
                            >
                                <DeleteIcon />
                            </IconButton>

                            {/* 🔴 Recording Indicator */}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: "50%",
                                        backgroundColor: "red",
                                        animation: "pulse 1.5s infinite",
                                        "@keyframes pulse": {
                                            "0%": { opacity: 1 },
                                            "50%": { opacity: 0.3 },
                                            "100%": { opacity: 1 },
                                        },
                                    }}
                                />
                                <Typography sx={{ color: "#333", fontWeight: 500 }}>
                                    Recording...
                                </Typography>
                            </Box>

                            {/* ⏸️ Stop Recording */}
                            <IconButton
                                sx={{
                                    backgroundColor: "red",
                                    color: "white",
                                    "&:hover": { backgroundColor: "#c40000" },
                                }}
                                onClick={stopRecording}
                            >
                                <StopIcon />
                            </IconButton>
                        </Box>
                    )}
                </>
            ) : (
                /* 💬 Normal Chat Input */
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: "1px solid #ddd",
                        padding: "8px 8px",
                        backgroundColor: "#fff",
                        borderRadius: "10px",

                        gap: 1,
                    }}
                >
                    {/* 📎 Attach Button */}
                    <IconButton onClick={() => fileInputRef.current.click()}>
                        <AttachFileIcon color="primary" />
                    </IconButton>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                    />

                    {/* 💬 Message Input */}
                    <TextField
                        variant="outlined"
                        placeholder="Type a message..."
                        size="small"
                        fullWidth
                        multiline
                        maxRows={3}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                            },
                        }}
                    />

                    {/* 🎤 / 📤 Icon */}
                    {inputText || selectedFile ? (
                        <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={loading}
                            sx={{
                                backgroundColor: "neutral.teal",
                                color: "white",
                                "&:hover": { backgroundColor: "neutral.teal" },
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
                        </IconButton>
                    ) : (
                        <IconButton
                            color="primary"
                            onClick={startRecording}
                            sx={{
                                backgroundColor: "#eaf1ff",
                                "&:hover": { backgroundColor: "#d3e0ff" },
                            }}
                        >
                            <MicIcon />
                        </IconButton>
                    )}

                    {/* <Box
                            // onClick={(e) => handleStatusClick(e, query?.id)}
                            sx={{
                                px: 1.5,
                                py: 1.5,
                                borderRadius: "12px",
                                backgroundColor: bg,
                                color: color,
                                fontSize: "12px",
                                fontWeight: 600,
                                cursor: "pointer",
                                textAlign: "center",
                                fontFamily: "Lato",
                                minWidth: "60px",
                            }}
                        >
                            {text}
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleCloseMenu}

                        >
                            <MenuItem onClick={() => handleChangeStatus('open', id)} sx={menuItemStyle}>Open</MenuItem>
                            <MenuItem onClick={() => handleChangeStatus('resolved', id)} sx={menuItemStyle}>Resolved</MenuItem>
                            <MenuItem onClick={() => handleChangeStatus('close', id)} sx={menuItemStyle}>Close</MenuItem>
                            <MenuItem onClick={() => handleChangeStatus('in_progress', id)} sx={menuItemStyle}>In Progress</MenuItem>
                        </Menu> */}
                </Box>
            )}
        </Box>
    )
}

export default ChatCardQuery;
